// Pure settings-history diff engine, shared by settings_history_diff_cell.vue (rendering)
// and admin_settings_history.vue (top-fields chip aggregation). No Vue/component state here —
// callers own their own memoization (a computed prop, or caching the result on the item).

function get_event_diff_patterns() {
  return [
    {
      prefix: 'Updated Cust PreTrip Alert Event: ',
      action: 'updated',
      label: 'Cust PreTrip Alert Event',
      settingsKey: 'customer_trip_notifications_alerts_settings',
      idKey: 'eventName',
    },
    {
      prefix: 'New Cust PreTrip Alert Event Added: ',
      action: 'added',
      label: 'Cust PreTrip Alert Event',
      settingsKey: 'customer_trip_notifications_alerts_settings',
      idKey: 'eventName',
    },
    {
      prefix: 'Deleted Cust PreTrip Alert Event: ',
      action: 'deleted',
      label: 'Cust PreTrip Alert Event',
      settingsKey: 'customer_trip_notifications_alerts_settings',
      idKey: 'eventName',
    },
    {
      prefix: 'New Yard Added: ',
      action: 'added',
      label: 'Yard',
      settingsKey: 'vehicle_yards',
      idKey: 'yard_name',
    },
    {
      prefix: 'Yard Deleted: ',
      action: 'deleted',
      label: 'Yard',
      settingsKey: 'vehicle_yards',
      idKey: 'yard_name',
    },
    {
      prefix: 'New HOS Event Added: ',
      action: 'added',
      label: 'HOS Event',
      settingsKey: 'driver_hos_alerts_settings',
      idKey: 'eventName',
    },
    {
      prefix: 'HOS Event Deleted: ',
      action: 'deleted',
      label: 'HOS Event',
      settingsKey: 'driver_hos_alerts_settings',
      idKey: 'eventName',
    },
    {
      prefix: 'New Vehicle Late Alert Event Added: ',
      action: 'added',
      label: 'Vehicle Late Alert Event',
      settingsKey: 'vehicle_late_alert_settings',
      idKey: 'eventName',
    },
    {
      prefix: 'Vehicle Late Alert Event Deleted: ',
      action: 'deleted',
      label: 'Vehicle Late Alert Event',
      settingsKey: 'vehicle_late_alert_settings',
      idKey: 'eventName',
    },
    {
      prefix: 'New Booking State Alert Event Added: ',
      action: 'added',
      label: 'Booking State Alert Event',
      settingsKey: 'booking_state_alert_settings',
      idKey: 'eventName',
    },
    {
      prefix: 'Booking State Alert Event Deleted: ',
      action: 'deleted',
      label: 'Booking State Alert Event',
      settingsKey: 'booking_state_alert_settings',
      idKey: 'eventName',
    },
  ]
}

export function is_diff_empty(item) {
  if (!item || item.diff == null) return true
  return String(item.diff).trim() === ''
}

function is_event_diff_string(diffStr) {
  if (!diffStr) return false
  const diff_string = String(diffStr)
  return get_event_diff_patterns().some(pattern => diff_string.startsWith(pattern.prefix))
}

function is_event_history_record(item) {
  return !!(item && item.diff && is_event_diff_string(item.diff))
}

function parse_python_repr(str) {
  const trimmed = String(str).trim()
  if (!trimmed) return null
  try {
    const normalized = normalize_python_jsondiff_repr(trimmed)
    return JSON.parse(normalized)
  } catch (e) {
    return null
  }
}

function unwrap_event_payload(payload) {
  if (Array.isArray(payload)) {
    if (payload.length === 1) return payload[0]
    return payload
  }
  return payload
}

function parse_event_diff_info(diffStr) {
  if (!diffStr) return null
  const diff_string = String(diffStr).trim()
  const pattern = get_event_diff_patterns().find(p => diff_string.startsWith(p.prefix))
  if (!pattern) return null

  const remainder = diff_string.slice(pattern.prefix.length).trim()
  let name = null
  let payload = null
  let patch = null

  if (pattern.action === 'updated') {
    const comma_idx = remainder.indexOf(', ')
    if (comma_idx === -1) return null
    name = remainder.slice(0, comma_idx).trim()
    const patch_str = remainder.slice(comma_idx + 2).trim()
    const json_portion = extract_jsondiff_portion(patch_str) || patch_str
    patch = parse_python_repr(json_portion)
  } else {
    payload = parse_python_repr(remainder)
    const obj = unwrap_event_payload(payload)
    if (obj && pattern.idKey && obj[pattern.idKey] != null) {
      name = String(obj[pattern.idKey])
    }
  }

  const root_path = name
    ? `${pattern.settingsKey}[${name}]`
    : pattern.settingsKey

  return {
    action: pattern.action,
    label: pattern.label,
    name: name || '(unknown)',
    rootPath: root_path,
    settingsKey: pattern.settingsKey,
    idKey: pattern.idKey,
    payload,
    patch,
  }
}

function find_settings_event(root, settingsKey, idKey, name) {
  const settings = root && root[settingsKey]
  if (!Array.isArray(settings) || !name) return null
  return settings.find(entry => entry && String(entry[idKey]) === String(name)) || null
}

function prefix_event_patch_paths(changes, rootPath) {
  return (changes || []).map(change => ({
    ...change,
    path: change.path ? `${rootPath}.${change.path}` : rootPath,
  }))
}

function build_event_summary_change(info) {
  const change_type = info.action === 'added' ? 'added' : 'removed'
  return [{
    type: change_type,
    path: info.rootPath,
    [change_type === 'added' ? 'new' : 'old']: info.name,
  }]
}

function is_jsondiff_patch_object(val) {
  return is_plain_object(val) && (
    '$delete' in val || '$insert' in val || '$update' in val || 'html_diff' in val
  )
}

function parse_event_diff_changes(item) {
  if (!item || !item.diff) return []
  const info = parse_event_diff_info(item.diff)
  if (!info) return []

  if (info.action === 'updated' && info.patch) {
    const old_event = find_settings_event(item.old, info.settingsKey, info.idKey, info.name)
    const new_event = find_settings_event(item.new, info.settingsKey, info.idKey, info.name)
    const changes = jsondiff_patch_to_changes(info.patch, '', old_event, new_event)
    return prefix_event_patch_paths(changes, info.rootPath)
      .filter(change => change.type !== 'changed' || !values_equal(change.old, change.new))
  }

  if (info.action === 'added' || info.action === 'deleted') {
    return build_event_summary_change(info)
  }

  return []
}

/**
 * Event add/update/delete handlers store a human-readable summary in `diff`.
 * The API often saves identical `old`/`new` snapshots (same object reference),
 * so we parse the diff string for field-level changes instead of dumping JSON.
 */
export function get_event_message(item) {
  if (!is_event_history_record(item)) return null
  const info = parse_event_diff_info(item.diff)
  if (!info) return String(item.diff).slice(0, 120) + (String(item.diff).length > 120 ? '…' : '')
  if (info.action === 'updated') return `Updated ${info.label}: ${info.name}`
  if (info.action === 'added') return `New ${info.label} Added: ${info.name}`
  return `${info.label} Deleted: ${info.name}`
}

function get_event_html_diff(item) {
  if (!is_event_history_record(item) || !item.htmldiff) return null
  const text = String(item.htmldiff).trim()
  return text || null
}

function parse_html_token_diff(htmlText) {
  if (!htmlText) return null
  const lines = String(htmlText).split('\n')
  const minus = []
  const plus = []
  for (const raw_line of lines) {
    const line = raw_line.replace(/\r$/, '')
    if (line.startsWith('-')) minus.push(line.slice(1).trim())
    else if (line.startsWith('+')) plus.push(line.slice(1).trim())
  }
  if (minus.length === 0 && plus.length === 0) return null
  return {
    old: minus.length ? minus.join('\n') : null,
    new: plus.length ? plus.join('\n') : null,
  }
}

export function get_event_html_parsed(item) {
  return parse_html_token_diff(get_event_html_diff(item))
}

export function show_event_block(item) {
  return !!(get_event_message(item) || get_event_html_diff(item))
}

function has_real_old_new_diff(item) {
  if (!item || item.old == null || item.new == null) return false
  try {
    return JSON.stringify(item.old) !== JSON.stringify(item.new)
  } catch (e) {
    return false
  }
}

function is_html_template_path(path) {
  if (!path) return false
  if (path === 'customer_trip_notifications_customer_html_template') return true
  if (path === 'driver_surveys_html_template') return true
  return path.includes('email_html_template')
}

function filter_deep_diff_changes(changes) {
  return (changes || []).filter(change => !is_html_template_path(change.path))
}

function should_hide_change_row(change) {
  if (!change) return true
  if (change.new === '<>') return true
  if (change.path === 'driver_count' || change.path === 'vehicle_count') return true
  if (change.path === '(api diff)') return true
  return false
}

function filter_visible_changes(changes) {
  return (changes || []).filter(change => !should_hide_change_row(change))
}

function build_htmldiff_changes(item) {
  if (!item || !item.htmldiff) return []
  const parsed = parse_html_token_diff(item.htmldiff)
  if (!parsed) return []
  return [{
    type: 'changed',
    path: 'customer_trip_notifications_customer_html_template',
    old: parsed.old,
    new: parsed.new != null ? parsed.new : String(item.htmldiff),
  }]
}

function normalize_python_jsondiff_repr(diffStr) {
  let out = String(diffStr).trim()
  out = out.replace(/\bdelete:/g, '"$delete":')
  out = out.replace(/\binsert:/g, '"$insert":')
  out = out.replace(/\bupdate:/g, '"$update":')
  out = out.replace(/\badd:/g, '"$add":')
  out = out.replace(/\bTrue\b/g, 'true')
  out = out.replace(/\bFalse\b/g, 'false')
  out = out.replace(/\bNone\b/g, 'null')
  out = out.replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (_, content) => JSON.stringify(content))
  out = out.replace(/\(\s*(\d+)\s*,\s*/g, '[$1, ')
  out = out.replace(/\)\s*(?=[,\]])/g, ']')
  return out
}

function extract_jsondiff_portion(diffStr) {
  const trimmed = String(diffStr).trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null
  if (!trimmed.startsWith('{')) return trimmed

  let depth = 0
  let in_string = false
  let string_quote = null
  let escaped = false

  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i]
    if (in_string) {
      if (escaped) {
        escaped = false
        continue
      }
      if (ch === '\\') {
        escaped = true
        continue
      }
      if (ch === string_quote) {
        in_string = false
        string_quote = null
      }
      continue
    }
    if (ch === '\'' || ch === '"') {
      in_string = true
      string_quote = ch
      continue
    }
    if (ch === '{') depth++
    else if (ch === '}') {
      depth--
      if (depth === 0) return trimmed.slice(0, i + 1)
    }
  }
  return trimmed
}

function resolve_path_value(root, path) {
  if (root == null || path == null || path === '') return root
  const segments = []
  const re = /([^.[\]]+)|\[([^\]]+)\]/g
  let match
  while ((match = re.exec(path)) !== null) {
    segments.push(match[1] !== undefined ? match[1] : match[2])
  }
  let current = root
  for (const seg of segments) {
    if (current == null) return undefined
    current = current[seg]
  }
  return current
}

function jsondiff_patch_to_changes(patch, pathPrefix, oldRoot, newRoot) {
  const changes = []
  if (patch == null || typeof patch !== 'object') return changes

  if (Array.isArray(patch)) {
    if (patch.length === 2 && pathPrefix) {
      changes.push({ type: 'changed', path: pathPrefix, old: patch[0], new: patch[1] })
    }
    return changes
  }

  for (const key of Object.keys(patch)) {
    if (key === '$delete') {
      for (const deleted_key of (patch[key] || [])) {
        const sub_path = pathPrefix ? `${pathPrefix}.${deleted_key}` : String(deleted_key)
        changes.push({
          type: 'removed',
          path: sub_path,
          old: resolve_path_value(oldRoot, sub_path),
        })
      }
      continue
    }
    if (key === '$insert') {
      for (const entry of (patch[key] || [])) {
        if (!Array.isArray(entry) || entry.length < 2) continue
        const sub_path = `${pathPrefix}[${entry[0]}]`
        const resolved_new = resolve_path_value(newRoot, sub_path)
        changes.push({
          type: 'added',
          path: sub_path,
          new: resolved_new != null ? resolved_new : entry[1],
        })
      }
      continue
    }
    if (key === '$update') {
      for (const [update_key, update_val] of Object.entries(patch[key] || {})) {
        const sub_path = pathPrefix ? `${pathPrefix}.${update_key}` : update_key
        const resolved_new = resolve_path_value(newRoot, sub_path)
        changes.push({
          type: 'changed',
          path: sub_path,
          old: resolve_path_value(oldRoot, sub_path),
          new: resolved_new != null ? resolved_new : update_val,
        })
      }
      continue
    }
    if (key === 'html_diff') continue

    const sub_path = pathPrefix ? `${pathPrefix}.${key}` : key
    const val = patch[key]
    if (Array.isArray(val) && val.length === 2) {
      changes.push({ type: 'changed', path: sub_path, old: val[0], new: val[1] })
      continue
    }
    if (is_plain_object(val) && !is_jsondiff_patch_object(val)) {
      changes.push(...jsondiff_patch_to_changes(val, sub_path, oldRoot, newRoot))
      continue
    }
    const old_val = resolve_path_value(oldRoot, sub_path)
    const new_val = resolve_path_value(newRoot, sub_path)
    if (old_val === undefined && new_val !== undefined) {
      changes.push({ type: 'added', path: sub_path, new: new_val != null ? new_val : val })
    } else if (old_val !== undefined && new_val === undefined) {
      changes.push({ type: 'removed', path: sub_path, old: old_val })
    } else if (old_val !== undefined || new_val !== undefined) {
      changes.push({
        type: 'changed',
        path: sub_path,
        old: old_val,
        new: new_val != null ? new_val : val,
      })
    } else {
      // jsondiff scalar updates only include the new value in the patch
      changes.push({ type: 'changed', path: sub_path, old: undefined, new: val })
    }
  }
  return changes
}

function parse_api_diff_changes(item) {
  if (!item || is_diff_empty(item)) return []
  const diff_string = String(item.diff).trim()
  if (is_event_diff_string(diff_string)) return []

  const json_portion = extract_jsondiff_portion(diff_string)
  if (!json_portion) return []

  try {
    const normalized = normalize_python_jsondiff_repr(json_portion)
    const patch = JSON.parse(normalized)
    const changes = jsondiff_patch_to_changes(patch, '', item.old, item.new)
    if (changes.length > 0) return changes
  } catch (e) {
    // unparseable diff — rely on deep_diff from old/new snapshots
  }

  return []
}

function merge_change_rows(primary, secondary) {
  const by_path = {}
  for (const change of primary) {
    by_path[change.path] = change
  }
  for (const change of secondary) {
    if (!by_path[change.path]) {
      by_path[change.path] = change
    }
  }
  return Object.values(by_path).sort((a, b) => a.path.localeCompare(b.path))
}

export function get_changes(item) {
  if (is_event_history_record(item)) {
    return filter_visible_changes(parse_event_diff_changes(item))
  }

  const deep_changes = has_real_old_new_diff(item)
    ? filter_deep_diff_changes(deep_diff(item.old, item.new, ''))
    : []
  // Only parse the API diff string when old/new snapshots are missing or identical
  const api_changes = (is_diff_empty(item) || deep_changes.length > 0)
    ? []
    : parse_api_diff_changes(item)
  let changes = merge_change_rows(deep_changes, api_changes)

  if (is_diff_empty(item) && item.htmldiff) {
    changes = merge_change_rows(changes, build_htmldiff_changes(item))
  }

  return filter_visible_changes(changes)
}

export function deep_diff(oldVal, newVal, path) {
  const changes = []
  _deep_diff_into(oldVal, newVal, path, changes)
  return changes
}

function _deep_diff_into(oldVal, newVal, path, changes) {
  if (values_equal(oldVal, newVal)) return

  const oldIsObj = is_plain_object(oldVal)
  const newIsObj = is_plain_object(newVal)
  const oldIsArr = Array.isArray(oldVal)
  const newIsArr = Array.isArray(newVal)

  if (oldIsObj && newIsObj) {
    const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)])
    const sorted = [...keys].sort()
    for (const k of sorted) {
      const sub_path = path ? `${path}.${k}` : k
      const old_has = Object.prototype.hasOwnProperty.call(oldVal, k)
      const new_has = Object.prototype.hasOwnProperty.call(newVal, k)
      if (old_has && new_has) {
        _deep_diff_into(oldVal[k], newVal[k], sub_path, changes)
      } else if (new_has) {
        changes.push({ type: 'added', path: sub_path, new: newVal[k] })
      } else {
        changes.push({ type: 'removed', path: sub_path, old: oldVal[k] })
      }
    }
    return
  }

  if (oldIsArr && newIsArr) {
    const id_key = detect_array_id_key(oldVal, newVal)
    if (id_key) {
      _diff_arrays_by_id(oldVal, newVal, path, id_key, changes)
    } else {
      const max_len = Math.max(oldVal.length, newVal.length)
      for (let i = 0; i < max_len; i++) {
        const sub_path = `${path}[${i}]`
        if (i < oldVal.length && i < newVal.length) {
          _deep_diff_into(oldVal[i], newVal[i], sub_path, changes)
        } else if (i < newVal.length) {
          changes.push({ type: 'added', path: sub_path, new: newVal[i] })
        } else {
          changes.push({ type: 'removed', path: sub_path, old: oldVal[i] })
        }
      }
    }
    return
  }

  changes.push({
    type: oldVal === undefined ? 'added' : (newVal === undefined ? 'removed' : 'changed'),
    path: path || '(root)',
    old: oldVal,
    new: newVal,
  })
}

function detect_array_id_key(oldArr, newArr) {
  const candidates = ['eventName', 'yard_name', 'name', 'id']
  const all = [...oldArr, ...newArr]
  if (all.length === 0) return null
  if (!all.every(item => is_plain_object(item))) return null
  for (const key of candidates) {
    if (all.every(item => key in item && item[key] != null)) {
      return key
    }
  }
  return null
}

function _diff_arrays_by_id(oldArr, newArr, path, id_key, changes) {
  const old_by_id = {}
  const new_by_id = {}
  for (const item of oldArr) old_by_id[String(item[id_key])] = item
  for (const item of newArr) new_by_id[String(item[id_key])] = item
  const ids = new Set([...Object.keys(old_by_id), ...Object.keys(new_by_id)])
  const sorted = [...ids].sort()
  for (const id of sorted) {
    const sub_path = `${path}[${id}]`
    const o = old_by_id[id]
    const n = new_by_id[id]
    if (o && n) {
      _deep_diff_into(o, n, sub_path, changes)
    } else if (n) {
      changes.push({ type: 'added', path: sub_path, new: n })
    } else {
      changes.push({ type: 'removed', path: sub_path, old: o })
    }
  }
}

export function values_equal(a, b) {
  if (a === b) return true
  if (a == null || b == null) return a == null && b == null
  if (typeof a !== typeof b) {
    // Compare numeric strings vs numbers (DynamoDB Decimal can show up either way)
    if ((typeof a === 'number' || typeof a === 'string') && (typeof b === 'number' || typeof b === 'string')) {
      return String(a) === String(b)
    }
    return false
  }
  if (typeof a !== 'object') return false
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch (e) {
    return false
  }
}

export function is_plain_object(val) {
  return val !== null && typeof val === 'object' && !Array.isArray(val)
}

export function humanize_path(path) {
  if (!path) return '(root)'
  return path
    .split('.')
    .map(seg => seg
      .replace(/\[([^\]]+)\]/g, ' › $1')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase()))
    .join(' › ')
}

// Top-level segment of a change path (before the first '.' or '['), used to group
// individual field changes into a "which settings area changed" facet (e.g. the top-5
// changed-fields chips on the cross-tenant Settings History admin page).
export function field_root(path) {
  if (!path) return '(root)'
  const match = /^[^.[]+/.exec(path)
  return match ? match[0] : path
}

export function badge_label(type) {
  if (type === 'added') return 'Added'
  if (type === 'removed') return 'Removed'
  return 'Changed'
}

export function format_value(val) {
  if (val === undefined || val === null) return '(empty)'
  if (val === '') return '(empty)'
  if (typeof val === 'boolean') return val ? 'true' : 'false'
  if (typeof val === 'number') return String(val)
  if (typeof val === 'string') {
    if (val.length > 400) return val.slice(0, 400) + '…'
    return val
  }
  try {
    const s = JSON.stringify(val, null, 2)
    if (s.length > 1500) return s.slice(0, 1500) + '\n… (truncated, see raw JSON)'
    return s
  } catch (e) {
    return String(val)
  }
}

export function pretty_json(val) {
  try {
    return JSON.stringify(val, null, 2)
  } catch (e) {
    return String(val)
  }
}
